"use client";

import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import Cookie from "js-cookie";
import { BASE_URL } from "../config/constants";
import { FaEye, FaEyeSlash } from "react-icons/fa";

import { useNavigate } from "react-router-dom"; // ✅ إضافة هذا السطر
const Active = () => {
  const { t } = useTranslation();
  const navigate = useNavigate(); // ✅ تعريف navigate

  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [newComment, setNewComment] = useState({});
  const [newPost, setNewPost] = useState("");
  const userData = useMemo(() => {
    try {
      const stored = localStorage.getItem("userData");
      return stored ? JSON.parse(stored) : null;
    } catch (parseError) {
      console.error("Failed to parse userData from localStorage:", parseError);
      return null;
    }
  }, []);

  const [currentPage, setCurrentPage] = useState(1); // الصفحة الحالية
  const [totalPages, setTotalPages] = useState(1); // إجمالي الصفحات
  const limit = 10; // عدد البوستات لكل صفحة

  const anonymousName = t("anonymousUser", { defaultValue: "مستخدم" });
  const anonymousInitials = t("anonymousInitials", { defaultValue: "؟" });

  const getDisplayName = (user) => {
    if (!user) return anonymousName;
    const first = user.firstName?.trim() || "";
    const last = user.lastName?.trim() || "";
    const name = `${first} ${last}`.trim();
    return name || anonymousName;
  };

  const getInitials = (user) => {
    if (!user) return anonymousInitials;
    const firstInitial = user.firstName?.[0]?.toUpperCase() || "";
    const lastInitial = user.lastName?.[0]?.toUpperCase() || "";
    const initials = `${firstInitial}${lastInitial}`.trim();
    return initials || anonymousInitials;
  };

  const handleProfileNavigation = (userId) => {
    if (!userId) return;
    navigate(`/users/${userId}`);
  };

  const isAuthenticated = Boolean(Cookie.get("token") && userData?.id);

  {
    /* حقل إضافة بوست جديد يظهر فقط في الصفحة الأولى */
  }

  // 🔹 جلب البوستات
  const fetchPosts = async (page = 1) => {
    try {
      setLoading(true);
      const response = await fetch(
        `${BASE_URL}/api/community?page=${page}&limit=${limit}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${Cookie.get("token")}`,
          },
        }
      );

      if (!response.ok) throw new Error("فشل في جلب البوستات");

      const data = await response.json();
      console.log("📌 API response:", data);

      const formatted = (data.data || []).map((post) => ({
        ...post,
        showComments: false,
      }));

      setPosts(formatted);
      setCurrentPage(page);
      setTotalPages(data.totalPages || 1); // تأكد أن الـ API بيرجع totalPages
    } catch (err) {
      setError("تعذر تحميل البوستات");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // 🔹 إضافة بوست جديد
  const handleAddPost = async () => {
    if (!newPost.trim()) return;

    const token = Cookie.get("token");
    if (!token) {
      alert("برجاء تسجيل الدخول لإضافة بوست");
      return;
    }

    try {
      const response = await fetch(`${BASE_URL}/api/community`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ text: newPost }),
      });

      if (!response.ok) throw new Error("فشل في إرسال البوست");

      setNewPost("");
      fetchPosts(currentPage); // إعادة تحميل البوستات للصفحة الحالية
    } catch (error) {
      console.error("خطأ أثناء إضافة البوست:", error);
      alert("حصل خطأ أثناء إضافة البوست. حاول تاني.");
    }
  };

  // 🔹 جلب التعليقات لبوست
  const fetchComments = async (id) => {
    try {
      const response = await fetch(`${BASE_URL}/api/community/${id}/comments`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${Cookie.get("token")}`,
        },
      });

      if (!response.ok) throw new Error("فشل في جلب التعليقات");

      const data = await response.json();
      setPosts((prev) =>
        prev.map((post) =>
          post.id === id
            ? { ...post, comments: data.data || [], showComments: true }
            : post
        )
      );
    } catch (error) {
      console.error("خطأ أثناء جلب التعليقات:", error);
    }
  };

  const handleAddComment = async (id) => {
    if (!newComment[id]?.trim()) return;
    const token = Cookie.get("token");
    if (!token) {
      alert("⚠️ لازم تسجل دخول الأول قبل كتابة تعليق");
      return;
    }

    try {
      const response = await fetch(`${BASE_URL}/api/community/${id}/comments`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ text: newComment[id] }),
      });

      if (!response.ok) throw new Error("فشل في إرسال التعليق");

      setNewComment({ ...newComment, [id]: "" });
      fetchComments(id);
    } catch (error) {
      console.error("خطأ أثناء إضافة التعليق:", error);
    }
  };

  const toggleComments = (id, show) => {
    setPosts((prev) =>
      prev.map((post) =>
        post.id === id ? { ...post, showComments: !show } : post
      )
    );

    if (!show) fetchComments(id);
  };

  useEffect(() => {
    fetchPosts();
  }, []);

  if (loading)
    return (
      <div className="mt-20 text-center text-blue-500">جاري التحميل...</div>
    );
  if (error)
    return <div className="mt-20 text-center text-red-500">{error}</div>;

  return (
    <div className="mt-20" dir="rtl">
      <section className="bg-main h-[35vh] md:h-[50vh] flex justify-center items-center mb-16">
        <h2 className="text-2xl md:text-5xl text-white font-semibold">
          {t("active")}
        </h2>
      </section>

      <div className="max-w-2xl mx-auto p-2 md:p-4  space-y-6">
        {/* كتابة بوست جديد  لايظهر الا في الصفحه الاولي*/}
        {currentPage === 1 && isAuthenticated && (
          <div className="bg-white shadow-md p-4 rounded-2xl flex flex-col gap-2">
            <div className="flex items-center gap-3 mb-2">
              <div className="flex-shrink-0 text-white w-12 h-12 rounded-full flex justify-center items-center font-bold text-lg bg-blue-950">
                {getInitials(userData)}
              </div>
              <span className="font-semibold text-gray-800">
                {getDisplayName(userData)}
              </span>
            </div>
            <textarea
              value={newPost}
              onChange={(e) => setNewPost(e.target.value)}
              className="w-full border rounded-xl p-3 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-blue-400"
              placeholder="اكتب بوست جديد..."
              rows={3}
            />
            <button
              onClick={handleAddPost}
              className="self-end bg-blue-500 text-white px-5 py-2 rounded-full hover:bg-blue-600 transition-colors"
            >
              إرسال
            </button>
          </div>
        )}
        {currentPage === 1 && !isAuthenticated && (
          <div className="bg-white shadow-md p-4 rounded-2xl text-center text-gray-600">
            {t("login_required_to_post", {
              defaultValue: "سجل الدخول لإضافة بوست جديد.",
            })}
          </div>
        )}

        {/* عرض البوستات */}
        {posts.map((post) => {
          const postOwner = post.user || userData;
          const profileTargetId = post.user?.id ?? userData?.id ?? null;
          const isOwnerCurrentUser = Boolean(userData?.id) && post.user?.id === userData?.id;

          return (
            <div
              key={post.id}
              className="bg-white shadow-md p-2 md:p-4 rounded-2xl space-y-3"
            >
              {/* Header البوست */}
              <div className="flex items-center gap-3">
                <button
                  className={`flex-shrink-0 text-white w-12 h-12 rounded-full flex justify-center items-center font-bold text-lg ${isOwnerCurrentUser ? "bg-blue-950" : "bg-blue-500"
                    }`}
                  onClick={() => handleProfileNavigation(profileTargetId)}
                >
                  {getInitials(postOwner)}
                </button>
                <div className="flex flex-col">
                  <h3 className="font-semibold text-gray-800">
                    {getDisplayName(postOwner)}
                  </h3>
                  <span className="text-gray-400 text-xs">
                    {post.createdAt
                      ? new Date(post.createdAt).toLocaleString("ar-EG")
                      : ""}
                  </span>
                </div>
              </div>
              <p className="text-gray-700">{post.text}</p>
              <button
                onClick={() => toggleComments(post.id, post.showComments)}
                className="border-blue-800 border-2 text-blue-800 p-2 px-4 rounded-full text-sm flex items-center gap-2"
              >
                {post.showComments ? <FaEyeSlash /> : <FaEye />}
                {post.showComments ? "إخفاء التعليقات" : "عرض التعليقات"}
              </button>

              {post.showComments && (
                <div className="mt-3 space-y-3 w-full">
                  {Array.isArray(post.comments) &&
                    post.comments.map((comment) => {
                      const commentOwner = comment.user || userData;
                      const commentOwnerId = comment.user?.id ?? userData?.id ?? null;
                      const isCommentByCurrentUser =
                        Boolean(userData?.id) && comment.user?.id === userData?.id;

                      return (
                        <div
                          key={comment.id}
                          className="flex gap-3 w-full overflow-hidden"
                        >
                          <button
                            className={`flex-shrink-0 w-10 h-10 rounded-full flex justify-center items-center font-bold text-sm text-white ${isCommentByCurrentUser ? "bg-blue-950" : "bg-blue-500"
                              }`}
                            onClick={() => handleProfileNavigation(commentOwnerId)}
                          >
                            {getInitials(commentOwner)}
                          </button>
                          <div className="bg-gray-100 rounded-2xl p-2 md:p-3 flex-1 shadow-sm relative">
                            <div className="flex items-center justify-between mb-1">
                              <span className="font-semibold text-gray-800 md:text-sm text-xs">
                                {getDisplayName(commentOwner)}
                              </span>
                              <span className="text-gray-400 text-xs text-wrap">
                                {new Date(comment.createdAt).toLocaleString("ar-EG")}
                              </span>
                            </div>
                            <p className="text-gray-800 text-sm">{comment.text}</p>
                          </div>
                        </div>
                      );
                    })}
                  <div className="flex gap-2 items-center mt-2 w-full">
                    <div className="flex-shrink-0 w-10 h-10 rounded-full flex justify-center items-center font-bold text-sm bg-blue-950 text-white">
                      {getInitials(userData)}
                    </div>
                    <input
                      type="text"
                      value={newComment[post.id] || ""}
                      onChange={(e) =>
                        setNewComment({
                          ...newComment,
                          [post.id]: e.target.value,
                        })
                      }
                      placeholder="اكتب تعليقك..."
                      className="flex-1 border rounded-full px-4 py-2 max-w-[170px] md:max-w-full text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                      disabled={!isAuthenticated}
                    />
                    <button
                      onClick={() => handleAddComment(post.id)}
                      className="bg-blue-500 text-white px-4 py-2 rounded-full hover:bg-blue-600 text-sm transition-colors disabled:bg-blue-300 disabled:cursor-not-allowed"
                      disabled={!isAuthenticated}
                    >
                      إرسال
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
        {/* Pagination */}
        <div className="flex justify-center gap-2 mt-6">
          {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
            <button
              key={page}
              onClick={() => fetchPosts(page)}
              className={`px-4 py-2 rounded-full ${currentPage === page
                  ? "bg-blue-500 text-white"
                  : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                }`}
            >
              {page}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Active;
